import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Icon from 'app/common/Icon/index';
import classes from './style.module.scss';
import { ServiceCheckPayload } from "types/index";
import { useStoreState } from "~/store";
import List from "~/issues/List/List";
import useTranslation from '~/app/hooks/useTranslation';
import { ServiceStatuses } from '~/app/data/statuses/services';
import useAccessControl from '~/app/hooks/useAccessControl';
import { PERMISSION_GROUPS } from '~/accessControl/permissionGroups';

export interface ListItemProps {
    item: ServiceCheckPayload
    onSelect?: (item: any, status: ServiceCheckPayload['status']) => void
    onRegisterIssue: (item: ServiceCheckPayload) => void
    isWorkOrderChecklist?: boolean
}

export type ListItemComponent = React.FC<ListItemProps>

const ListItem: ListItemComponent = ({ item, onSelect, onRegisterIssue, isWorkOrderChecklist = false }) => {

    const { t } = useTranslation('serviceChecklist');
    const [...allIssues] = useStoreState(state => state.machine.data.issues);

    const { checkPermission } = useAccessControl();
    const writeServiceDetailsPermission = checkPermission(PERMISSION_GROUPS.writeServiceDetails);
    const hasSimplifiedWorkorder = useStoreState(state => state.service.hasSimplifiedWorkorder ?? false);
    const serviceStatus = useStoreState(state => state.service.data.status);
    const workOrderNo = useStoreState(state => state.service.data.workOrderNo);

    const isServiceChecklistEnabled = useMemo(() => {
        return (hasSimplifiedWorkorder || isWorkOrderChecklist) && ((serviceStatus === ServiceStatuses.TRIGGERED) || (serviceStatus === ServiceStatuses.INPROGRESS && workOrderNo));
    }, [hasSimplifiedWorkorder, serviceStatus, workOrderNo, isWorkOrderChecklist]);

    const statusChangeHandler = useCallback((newStatus) => {
        if (isServiceChecklistEnabled && (writeServiceDetailsPermission || isWorkOrderChecklist) && onSelect) {
            onSelect(item, (newStatus === item.status) ? null : newStatus);
        }
    }, [isServiceChecklistEnabled, writeServiceDetailsPermission, onSelect, item, isWorkOrderChecklist]);

    const serviceCheckIssues = useMemo(() => {
        const issueIds = item.issues.map(issue => Number(issue));
        return allIssues.reverse().filter(issue => issueIds.includes(Number(issue.id)));
    }, [item.issues, allIssues]);

    return (
        <>
            <div className={classes['list-item-wrapper']}>
                <div className={`${classes['list-item']} ${serviceCheckIssues.length ? classes['show-issues'] : ''}`}>
                    <div className={`${classes['title']} ${item.sectionId && !item.isServiceSection ? classes['add-tab'] : ''}`}>{item.sequenceNumber} {item.name}</div>
                    {item.isServiceSection === false && (
                        <div className={classNames(classes['icon-wrap'])}>
                            {!item.isRequired && (
                                <button
                                    type="button"
                                    className={`${classes['na-btn']} ${item.status === 2 ? classes['checked'] : ''} ${(item.status === 1 || item.status === 0) ? classes['disabled'] : ''}`}
                                    onClick={() => statusChangeHandler(2)}
                                >
                                    {t('notApplicable')}
                                </button>
                            )}
                            <Icon name={'close'}
                                className={`${classes['cancel']} ${item.status === 0 ? classes['checked'] : ''} ${(item.status === 1 || item.status === 2) ? classes['disabled'] : ''}`}
                                onClick={() => statusChangeHandler(0)}
                            />
                            <Icon name={'done'}
                                className={`${classes['ok']} ${item.status === 1 ? classes['checked'] : ''} ${(item.status === 2 || item.status === 0) ? classes['disabled'] : ''}`}
                                onClick={() => statusChangeHandler(1)}
                            />
                        </div>
                    )}
                </div>
            </div>
            {serviceCheckIssues.length > 0 && (
                <>
                    <List list={serviceCheckIssues} listClassName={'periodic-check-issues'} />
                    {isServiceChecklistEnabled && item.status !== 1 && <div className={classes['register-issue-link']} onClick={() => onRegisterIssue(item)}>{t('registerNewIssue')}</div>}
                </>
            )}
        </>
    );
};

export default ListItem
